import React from 'react';
import { Formik, Form, Field, CccisdInput, CccisdFieldWrapper } from 'cccisd-formik';
import Table from 'cccisd-graphql-table';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import RequiredFieldLabel from './RequiredFieldLabel';
import sitesQuery from '../../queries/sites.graphql';
import instructorsQuery from '../../queries/instructors.graphql';

const StudentForm = props => {
    const { onSubmit, initialValues } = props;

    const onHandleSubmit = async values => {
        values.parentPawns = values.instructor;
        values.site = values.site;
        values.district = null;
        values.state = null;
        values.role = 'learner';

        await onSubmit(values);
    };

    const validate = values => {
        const errors = {};

        if (!values.studentId) {
            errors.studentId = 'Student ID is required.';
        }

        if (values.email) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
                errors.email = 'Invalid email address format.';
            }
        }

        if (values.phone) {
            if (!/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(values.phone)) {
                errors.phone = 'Must be 10 characters long.';
            }
        }
        if (values.site.length < 1) {
            errors.site = 'Site is required.';
        }
        if (values.site.length > 1) {
            errors.site = 'You may select only 1 Site.';
        }

        if (values.instructor < 1) {
            errors.instructor = 'Provider is required.';
        }
        if (values.instructor.length > 1) {
            errors.instructor = 'You may select only 1 Provider.';
        }

        return errors;
    };

    const siteColumns = [
        {
            name: 'group.groupId',
            label: 'ID',
            hideInTable: true,
        },
        {
            name: 'group.label',
            label: 'Site',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.groupingUnit.group.label',
            label: 'District',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.organization.group.label',
            label: 'State',
            sort: true,
            filter: true,
        },
    ];

    const instructorColumns = [
        {
            name: 'pawn.pawnId',
            label: 'ID',
            hideInTable: true,
        },
        {
            name: 'user.fullName',
            label: 'Instructor',
            sort: true,
            filter: true,
        },
        {
            name: 'parentGroup.groupingUnit.group.label',
            label: 'District',
            sort: true,
            filter: true,
        },
    ];

    return (
        <Formik onSubmit={e => onHandleSubmit(e)} validate={e => validate(e)} initialValues={initialValues}>
            {({ handleSubmit, values, setFieldValue }) => (
                <Form>
                    <Field
                        name="studentId"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>Student ID:</RequiredFieldLabel>}
                    />
                    <Field name="firstName" component={CccisdInput} label="First Name:" />
                    <Field name="lastName" component={CccisdInput} label="Last Name:" />
                    <Field name="email" component={CccisdInput} label="Email:" />
                    <Field name="phone" component={CccisdInput} label="Phone Number:" placeholder="XXXXXXXXXX" />

                    <Field name="site" enableReinitialize>
                        {({ field, form }) => {
                            return (
                                <CccisdFieldWrapper
                                    field={field}
                                    form={form}
                                    label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                                >
                                    <Table
                                        query={sitesQuery}
                                        rowKey="group.groupId"
                                        columns={siteColumns}
                                        csvFilename="Sites.csv"
                                        showRowActions={false}
                                        rowSelectedCallback={selectedRows => {
                                            if (!_isEqual(selectedRows, field.value)) {
                                                setFieldValue('site', selectedRows);
                                                setFieldValue('instructor', []);
                                            }
                                        }}
                                        selectedRows={initialValues.site}
                                        maxSelectableRows={1}
                                        rowActions={[
                                            {
                                                name: 'selectSite',
                                                title: 'Add to Site',
                                                action: () => {},
                                            },
                                        ]}
                                    />
                                </CccisdFieldWrapper>
                            );
                        }}
                    </Field>
                    {values.site && values.site.length === 1 && (
                        <>
                            <Field name="instructor" enableReinitialize>
                                {({ field, form }) => {
                                    return (
                                        <CccisdFieldWrapper
                                            field={field}
                                            form={form}
                                            label={<RequiredFieldLabel isRequired>Provider:</RequiredFieldLabel>}
                                        >
                                            <Table
                                                query={instructorsQuery}
                                                rowKey="pawn.pawnId"
                                                columns={instructorColumns}
                                                csvFilename="Providers.csv"
                                                showRowActions={false}
                                                rowSelectedCallback={selectedRows => {
                                                    if (!_isEqual(selectedRows, field.value)) {
                                                        setFieldValue('instructor', selectedRows);
                                                    }
                                                }}
                                                selectedRows={
                                                    !_isEqual(form.values.site, initialValues.site)
                                                        ? field.value
                                                        : initialValues.instructor
                                                }
                                                graphqlVariables={{
                                                    siteId: values.site[0],
                                                }}
                                                maxSelectableRows={1}
                                                rowActions={[
                                                    {
                                                        name: 'selectProvider',
                                                        title: 'Link to Provider',
                                                        action: () => {},
                                                    },
                                                ]}
                                            />
                                        </CccisdFieldWrapper>
                                    );
                                }}
                            </Field>
                        </>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '1em',
                        }}
                    >
                        <button type="button" className="btn btn-primary" onClick={() => handleSubmit()}>
                            Submit
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

StudentForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
};

export default StudentForm;
